import type React from "react";
import { useEffect, useState } from "react";
import Column from "@/atoms-react/column/Column.react";
import { LayoutTitle } from "../../../_layout/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { DocumentRow } from "@/molecules/document-row/index.react";
import { useUser } from "@/features/user/hooks/use-user";
import { Skeletton } from "@/atoms-react/skeletton/index.react";
import { DragAndDrop } from "../drag-drop/index.react";
import type { Document } from "@/features/user/document/types";

import { DocumentViewer } from "./document-viewer.react";
import { useDocument } from "@/features/user/document/hooks/use-document";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "@/atoms-react/loader/Loader.react";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useCompany } from "@/features/company/hooks/use-company";
import { MandateService } from "@/features/company/types";
import { Alert, AlertType } from "@/atoms-react/alert/index.react";
import { InfoAlert } from "@/atoms-react/text/Text.react";

type AliasesDocumentsAppProps = { isOnboarding?: boolean } & React.ComponentPropsWithoutRef<"div">;

export const AliasesDocumentsApp = ({
  isOnboarding = false,
  ...props
}: AliasesDocumentsAppProps) => {
  const { t } = useTranslation(["pass"]);

  const { useUserProfile } = useUser();
  const { data: user } = useUserProfile();
  const [params] = useSearchParams();
  const { DocumentsDetailsQuery, UploadDocumentMutation } = useDocument();
  const {
    data: documents,
    isLoading: isLoadingDocuments,
    refetch
  } = DocumentsDetailsQuery(user?.naturalPerson?.UUID ?? null);
  const { mutateAsync: uploadDocument } = UploadDocumentMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [filterDocLoading, setFilterDocLoading] = useState<boolean>(false);
  const { checkPermission } = useUserCheckAccess();
  const { companyDetailQuery } = useCompany();

  const { leUuid } = useCompanyContext();
  const { data: companyDetails } = companyDetailQuery(leUuid);

  const [searchDocument, setSearchDocument] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[] | null>();
  const { getLanguage } = usei18n();
  const enabled = (() =>
    !leUuid
      ? true
      : !!companyDetails?.legalEntity.AgentReviewFinish &&
        checkPermission(MandateService.ALIASES_MANAGEMENT, user, companyDetails))();

  useEffect(() => {
    setFilterDocLoading(true);
    if (selectedDocument === null) {
      const previewUUID = params.get("preview");
      const found = documents?.find((doc) => doc.uuid === previewUUID);

      if (found) {
        setSelectedDocument(found);
      }
    }
    const filteredDocuments = documents?.filter((document) =>
      document.typePerLanguage.fr.toLowerCase().includes(searchDocument.toLowerCase())
    );
    setFilteredDocuments(filteredDocuments);
    setFilterDocLoading(false);
  }, [documents, params]);

  const handleDocumentClick = (document: Document) => {
    if (document.Document) {
      setSelectedDocument((prev) => (prev?.uuid === document.uuid ? null : document));
    } else {
      setSelectedDocument(null);
    }
  };

  const onUpload = async (files: FileList | null) => {
    if (!files || loading) {
      return;
    }
    setLoading(true);
    const toBase64 = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
      });
    const documentCountBeforeUpload = documents?.length || 0;
    const base64 = await toBase64(files[0]);
    await uploadDocument({
      ownerUUID: user?.naturalPerson?.UUID ?? "",
      language: getLanguage(),
      document: {
        base64Doc: base64,
        fileFormat: files[0].type,
        fileName: files[0].name
      }
    });
    const { data: newDocuments } = await refetch();
    if ((newDocuments?.length || 0) > documentCountBeforeUpload) {
      toast.success(t("aliasDocumentsPage.successfully_added"));
    } else {
      toast.error(t("aliasDocumentsPage.error_import"));
    }
    setLoading(false);
  };
  return (
    <Column {...props} className="flex flex-col">
      {!isOnboarding && <LayoutTitle>Documents</LayoutTitle>}

      {!enabled && !companyDetails?.legalEntity.UserValidation && leUuid && (
        <Alert title={t("")} className="mt-4 mb-4" type={AlertType.Warning}>
          <InfoAlert className="text-warning">
            {t("aliasDocumentsPage.warning_company_upload")}
          </InfoAlert>
        </Alert>
      )}
      <div className="-mx-8 flex h-full flex-1 flex-col overflow-hidden">
        <div className="flex-1 border-slate-50 border-t-2 md:flex">
          {/* Documents list, drag & drop (left part) */}
          <div className="w-full flex-1 border-slate-50 p-8 md:flex-1 md:border-r-2">
            <DragAndDrop
              disabled={!enabled}
              className="2xl:h-52"
              onValidated={onUpload}
              isLoading={loading}
            />
            <InputLabel
              placeholder={t("aliasDocumentsPage.search_document")}
              value={searchDocument}
              noLatinCheck={true}
              onChange={(value) => {
                if (!enabled) return;
                setSearchDocument(value);
              }}
              disabled={!enabled}
              className="mt-4 mb-3"
            />
            <div className="flex flex-col gap-y-4 overflow-y-auto">
              {filteredDocuments && filteredDocuments.length > 0 ? (
                filteredDocuments.map((document, index) => (
                  <DocumentRow
                    document={document}
                    key={index}
                    disabled={!enabled}
                    className="rounded-sm"
                    isSelected={document.uuid === selectedDocument?.uuid ? true : false}
                    onClick={() => handleDocumentClick(document)}
                  />
                ))
              ) : (
                <></>
              )}
              {filterDocLoading && <Loader />}
              {isLoadingDocuments &&
                [0, 1, 2].map((_, index) => <Skeletton key={index} className="mx-6 mb-1" />)}
            </div>
          </div>
          <div className="w-full border-b-2 md:hidden"></div>
          {/* Preview (right part) */}
          {selectedDocument && user && (
            <DocumentViewer
              user={user}
              document={selectedDocument}
              className="flex min-h-[200px] flex-col md:flex-1"
            />
          )}
        </div>
      </div>
    </Column>
  );
};
